import {runInAction} from 'mobx';

import type {CoreOrB2b} from '../../utils/coreOrB2b/types';
import type {OmitRecursively} from '../../utils/utility-types';
import type {
  BNCoachCoursesExtendParams,
  BNCoachCoursesForceStopParams,
  BNCoachCoursesUpdateParams,
} from '../types/bnrks/methods';
import type {
  CoreCoachCoursesCreateParams,
  CoreCoachCoursesDeleteParams,
  CoreCoachCoursesForceStopParams,
  CoreCoachCoursesMoveParams,
  CoreCoachCoursesPauseParams,
  CoreCoachCoursesReadParams,
  CoreCoachCoursesResumeParams,
  CoreCoachCoursesStartParams,
  CoreCoachCoursesStopParams,
  CoreCoachCoursesStopRestartingParams,
  CoreCoachCoursesUpdateParams,
} from '../types/core/methods';
import type {CourseApiModel} from './types';

import {pickCoreOrB2b} from '../../utils/coreOrB2b/pickCoreOrB2b';
import {apiRequest} from '../utils/apiRequest';
import {EntityStore} from '../utils/entityStore';

class CourseStore<
  T extends CourseApiModel = CourseApiModel,
> extends EntityStore<
  T,
  CoreCoachCoursesReadParams,
  CoreCoachCoursesCreateParams,
  CoreOrB2b<CoreCoachCoursesUpdateParams, BNCoachCoursesUpdateParams>,
  CoreCoachCoursesDeleteParams
> {
  move = this._createMethod<CoreCoachCoursesMoveParams>({
    method: 'coach.courses.move',
  });
  pause = this._createMethod<CoreCoachCoursesPauseParams>({
    method: 'coach.courses.pause',
  });
  resume = this._createMethod<CoreCoachCoursesResumeParams>({
    method: 'coach.courses.resume',
  });
  start = this._createMethod<CoreCoachCoursesStartParams>({
    method: 'coach.courses.start',
  });
  stop = this._createMethod<CoreCoachCoursesStopParams>({
    method: 'coach.courses.stop',
  });
  stopRestarting = this._createMethod<CoreCoachCoursesStopRestartingParams>({
    method: 'coach.courses.stop_restarting',
  });
  private internalForceStop = this._createMethod<
    BNCoachCoursesForceStopParams | CoreCoachCoursesForceStopParams
  >({
    method: 'coach.courses.force_stop',
  });

  async extendB2b(entity: T, params: BNCoachCoursesExtendParams) {
    try {
      runInAction(() => {
        this.updating = {
          entity,
          error: null,
          inProgress: true,
          success: false,
        };
      });

      const result = await apiRequest({
        method: 'coach.courses.extend',
        params,
      });

      const updatedEntity = this._processResult(result, params);

      runInAction(() => {
        this.updating = {
          ...this.updating,
          entity: updatedEntity,
          inProgress: false,
          success: true,
        };
      });

      return updatedEntity;
    } catch (error) {
      runInAction(() => {
        this.updating = {
          ...this.updating,
          error,
          inProgress: false,
        };
      });

      throw error;
    }
  }

  forceStop(
    course: T,
    {
      b2b,
      core,
    }: OmitRecursively<
      CoreOrB2b<CoreCoachCoursesForceStopParams, BNCoachCoursesForceStopParams>,
      '_id'
    > = {},
  ) {
    const params = pickCoreOrB2b<
      Omit<CoreCoachCoursesForceStopParams, '_id'>,
      Omit<BNCoachCoursesForceStopParams, '_id'>
    >({
      b2b,
      core,
    });

    return this.internalForceStop(course, params);
  }
}

export const courseStore = new CourseStore('course', {
  create: 'coach.courses.create',
  delete: 'coach.courses.delete',
  fetch: 'coach.courses.read',
  update: 'coach.courses.update',
});
